// Which version of the site this page is: the saved version it was built from (short id, its time, its title), put
// into index.html by vite.config.ts (<meta name="site-version">). The build also writes it to /version.json (no-cache,
// as every page of the site): a page asks for it now and then, and says when a newer version is on the site, with
// Reload. A page opened before a deploy keeps running the code it loaded until it is reloaded.
import { useEffect, useState } from 'react';
import { hubDateTime } from './format.ts';

/** A saved version: its short id, when it was saved (UTC ISO), its title; when the site was built from it. */
export interface SiteVersion {
  sha: string;
  at: string;
  subject: string;
  built: string;
}

/** The stamp as index.html holds it (the meta's content, JSON); null when there is none or it names no version. */
export function readSiteVersion(raw: string | null | undefined): SiteVersion | null {
  if (!raw) return null;
  try {
    const v = JSON.parse(raw) as Partial<SiteVersion> | null;
    if (!v || typeof v.sha !== 'string' || !v.sha) return null;
    return { sha: v.sha, at: String(v.at ?? ''), subject: String(v.subject ?? ''), built: String(v.built ?? '') };
  } catch {
    return null;
  }
}

/** This page's version; none without the page's stamp (a test run, a page built outside git). */
export const SITE_VERSION: SiteVersion | null =
  typeof document === 'undefined' ? null : readSiteVersion(document.querySelector('meta[name="site-version"]')?.getAttribute('content'));

/** How often the page asks whether a newer version is on the site. */
const ASK_MS = 5 * 60_000;

/** "b884fee · 2026-10-28 13:29": the short id and the saved version's Vilnius time. */
export const versionText = (v: SiteVersion): string => `${v.sha} · ${hubDateTime(v.at)}`;

/** The site's version now, when it is not this page's; else null. Asked on opening, when the window comes back, and every few minutes. */
export function useNewerVersion(mine: SiteVersion | null = SITE_VERSION): SiteVersion | null {
  const [newer, setNewer] = useState<SiteVersion | null>(null);
  useEffect(() => {
    // The development server has no /version.json: its page is the working tree.
    if (!mine || import.meta.env.DEV) return;
    let gone = false;
    const ask = async () => {
      try {
        const r = await fetch('/version.json', { cache: 'no-store' });
        if (!r.ok) return;
        const v = (await r.json()) as Partial<SiteVersion>;
        if (!gone && typeof v.sha === 'string' && v.sha && v.sha !== mine.sha) setNewer({ sha: v.sha, at: String(v.at ?? ''), subject: String(v.subject ?? ''), built: String(v.built ?? '') });
      } catch {
        // Offline, or the site is being deployed: asked again later.
      }
    };
    void ask();
    const timer = setInterval(() => void ask(), ASK_MS);
    const onFocus = () => void ask();
    window.addEventListener('focus', onFocus);
    return () => {
      gone = true;
      clearInterval(timer);
      window.removeEventListener('focus', onFocus);
    };
  }, [mine]);
  return newer;
}

/** The version, small, in the header: its title and build time on hover. */
export function SiteVersionLabel() {
  const v = SITE_VERSION;
  if (!v) return null;
  const local = import.meta.env.DEV ? ' · this computer' : '';
  return (
    <span className="hub-ver" title={`Site version ${v.sha}, saved ${hubDateTime(v.at)}: ${v.subject}${v.built ? ` · built ${hubDateTime(v.built)}` : ''}${local ? ' · the development server on this computer: its pending changes too' : ''}`}>
      v {v.sha}
      {local}
    </span>
  );
}

/** Work a page still has on its way (a save waiting, a text being translated): Reload waits for it first. */
const reloadWaits = new Set<{ wait: () => Promise<unknown> }>();

/** Reload in the bar waits for `wait` first, asked at the click; a failure does not stop it. Returns what takes it off. */
export function onReload(wait: () => Promise<unknown>): () => void {
  const entry = { wait };
  reloadWaits.add(entry);
  return () => void reloadWaits.delete(entry);
}

/** Reloads once every registered wait has answered; at once when nothing waits. */
async function reloadWhenSaved() {
  if (reloadWaits.size) await Promise.allSettled([...reloadWaits].map(async (e) => e.wait()));
  window.location.reload();
}

/** A newer version is on the site than the one this page runs: say so, with Reload. */
export function NewVersionBar() {
  const newer = useNewerVersion();
  if (!newer) return null;
  return (
    <div className="ver-banner" role="status">
      A newer version of the site is out ({versionText(newer)}{newer.subject ? `: ${newer.subject}` : ''}). This page still runs {SITE_VERSION ? SITE_VERSION.sha : 'an older one'}.{' '}
      <button type="button" className="btn small" onClick={() => void reloadWhenSaved()}>
        Reload
      </button>
    </div>
  );
}
